import type {
  LearningObjectAlternativeSection,
  LearningObjectCompletionCriteria,
  LearningObjectStructureEntry,
  ReferencedCourse,
  ReferencedLearningActivity
} from "../types";

export type StructuredLearningObject = ReferencedCourse | ReferencedLearningActivity;

export type StructuredLearningObjectItem = {
  completionCriteria: LearningObjectCompletionCriteria;
  entity: StructuredLearningObject;
  isRequiredInStructure?: boolean;
  kind: "learning-object";
  structureIndex: number;
};

export type StructuredAlternativeSectionItem = {
  alternatives: StructuredLearningObject[];
  completionCriteria: "ALTERNATIVE";
  id: number;
  kind: "alternative-section";
  selectedItem: StructuredLearningObject | null;
  structureIndex: number;
  title?: string;
};

export type StructuredContentItem =
  | StructuredLearningObjectItem
  | StructuredAlternativeSectionItem;

export type VisibleStructuredLearningObject = {
  alternativeSectionId?: number;
  entity: StructuredLearningObject;
  isRequiredInStructure?: boolean;
};

export type NormalizeLearningObjectStructureResult = {
  items: StructuredContentItem[];
  source: "legacy" | "structure";
};

type NormalizeLearningObjectStructureInput = {
  courses?: readonly ReferencedCourse[];
  learningActivities?: readonly ReferencedLearningActivity[];
  structure?: readonly LearningObjectStructureEntry[];
};

const isAlternativeSection = (
  entry: LearningObjectStructureEntry
): entry is LearningObjectAlternativeSection =>
  "type" in entry && entry.type === "SECTION";

const getOrder = (item: StructuredLearningObject): number =>
  Number(item.prereqOrderNumber) || 0;

export const normalizeLearningObjectStructure = ({
  courses = [],
  learningActivities = [],
  structure
}: NormalizeLearningObjectStructureInput): NormalizeLearningObjectStructureResult => {
  const completeItems: StructuredLearningObject[] = [
    ...courses,
    ...learningActivities
  ];

  if (!Array.isArray(structure)) {
    return {
      items: completeItems
        .slice()
        .sort((first, second) => getOrder(first) - getOrder(second))
        .map((entity, structureIndex) => ({
          completionCriteria: null,
          entity,
          kind: "learning-object",
          structureIndex
        })),
      source: "legacy"
    };
  }

  const itemsById = new Map<number, StructuredLearningObject>();
  completeItems.forEach((item) => itemsById.set(Number(item.id), item));

  const items = structure.flatMap<StructuredContentItem>((entry, structureIndex) => {
    if (!isAlternativeSection(entry)) {
      const entity = itemsById.get(Number(entry.id));
      if (!entity) return [];

      return [{
        completionCriteria: entry.completion_criteria,
        entity,
        isRequiredInStructure: entry.completion_criteria?.toUpperCase() === "REQUIRED",
        kind: "learning-object",
        structureIndex
      }];
    }

    const alternatives = entry.lo.flatMap((id) => {
      const entity = itemsById.get(Number(id));
      return entity ? [entity] : [];
    });
    const selectedItem = alternatives.find((item) => item.bookmarkedInSection) ?? null;

    return [{
      alternatives,
      completionCriteria: "ALTERNATIVE",
      id: Number(entry.id),
      kind: "alternative-section",
      selectedItem,
      structureIndex,
      ...(entry.title ? { title: entry.title } : {})
    }];
  });

  return { items, source: "structure" };
};

export const getVisibleStructuredLearningObjects = (
  items: readonly StructuredContentItem[]
): VisibleStructuredLearningObject[] => items.flatMap((item) => {
  if (item.kind === "learning-object") {
    return [{
      entity: item.entity,
      isRequiredInStructure: item.isRequiredInStructure
    }];
  }

  if (!item.selectedItem) return [];

  return [{
    alternativeSectionId: item.id,
    entity: item.selectedItem,
    isRequiredInStructure: true
  }];
});
